import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import { getCurrentConference } from '../../../base/conference/functions';
import { translate } from '../../../base/i18n/functions';
import ListItem from '../../../base/ui/components/web/ListItem';
import { ACTION_TRIGGER, AudioStateIcons, MEDIA_STATE, ParticipantAuthIndicatorIcon, PARTICIPANT_AUTHKEY_STATES, VideoStateIcons } from '../../constants';
import { getParticipantById, isLocalParticipantModerator } from '../../../base/participants/functions';
import { getParticipantVerificationStatus } from '../../../e2ee/functions';
import { RaisedHandIndicator } from './RaisedHandIndicator';
const useStyles = makeStyles()(theme => {
    return {
        nameContainer: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden'
        },
        name: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        moderatorLabel: {
            ...theme.typography.labelBold,
            color: theme.palette.text03
        },
        avatar: {
            marginRight: theme.spacing(3)
        }
    };
});
/**
 * A component representing a participant entry in ParticipantPane and Lobby.
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactNode}
 */
function ParticipantItem({ actionsTrigger = ACTION_TRIGGER.HOVER, audioMediaState = MEDIA_STATE.NONE, children, disableModeratorIndicator, displayName, isHighlighted, isModerator, local, onLeave, openDrawerForParticipant, overflowDrawer, participantID, raisedHand, t, videoMediaState = MEDIA_STATE.NONE, youText, showVerificationStatus = false }) {
    const participant = useSelector((state) => participantID ? getParticipantById(state, participantID) : undefined);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const isE2EEEnabled = useSelector((state) => {
        try {
            return Boolean(getCurrentConference(state)?.isE2EEEnabled?.());
        }
        catch {
            return false;
        }
    });
    const onClick = useCallback(() => openDrawerForParticipant?.({
        participantID,
        displayName
    }), [displayName, openDrawerForParticipant, participantID]);
    const { classes } = useStyles();
    if (!participantID) {
        return null;
    }
    const participantEmail = participant?.email ? participant.email : 'No email';
    const isParticipantVerified = getParticipantVerificationStatus(participantID);
    const participantAuthIndicatorIcon = ParticipantAuthIndicatorIcon[isParticipantVerified ? PARTICIPANT_AUTHKEY_STATES.PASSED : PARTICIPANT_AUTHKEY_STATES.UNPASSED];
    const icon = (React.createElement(Avatar, { className: classes.avatar, displayName: displayName, participantId: participantID, size: 32 }));
    const text = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes.nameContainer },
            React.createElement("div", { className: classes.name }, displayName),
            local ? React.createElement("span", null,
                "\u00A0(",
                youText,
                ")") : null),
        isModerator && !disableModeratorIndicator && React.createElement("div", { className: classes.moderatorLabel }, t('videothumbnail.moderator')),
        React.createElement("div", { className: classes.moderatorLabel }, participantEmail)));
    const indicators = (React.createElement(React.Fragment, null,
        isE2EEEnabled
            && isLocalModerator
            && !isModerator
            && showVerificationStatus
            && participantAuthIndicatorIcon,
        raisedHand && React.createElement(RaisedHandIndicator, null),
        VideoStateIcons[videoMediaState],
        AudioStateIcons[audioMediaState]));
    return (React.createElement(ListItem, { actions: children, hideActions: local, icon: icon, id: `participant-item-${participantID}`, indicators: indicators, isHighlighted: isHighlighted, onClick: !local && overflowDrawer ? onClick : undefined, onMouseLeave: onLeave, textChildren: text, trigger: actionsTrigger }));
}
export default translate(ParticipantItem);
